import { Layout } from "@/components/Layout";
import { SeoHead } from "@/components/SeoHead";
import { Link } from "react-router-dom";
import { Wrench, Zap, AlertTriangle, Droplets, Filter, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PHONE_HREF, PHONE_NUMBER } from "@/components/EmergencyBanner";

import wellCasingImg from "@/assets/well-casing-pump-installation.webp";
import wellPullingImg from "@/assets/well-pump-pulling-equipment-repair.webp";
import grinderInstallImg from "@/assets/grinder-pump-installation-excavator.webp";
import sewagePumpImg from "@/assets/sewage-pump-basin-installation.webp";
import openWellImg from "@/assets/open-well-pump-repair-poconos.webp";
import truckImg from "@/assets/semper-fi-well-pump-service-truck.webp";

const services = [
  { icon: Wrench, title: "Well Pump Repair", desc: "Fast diagnosis and repair for all well pump types including submersible, jet, and constant pressure systems.", href: "/services/well-pump-repair", img: wellCasingImg, imgAlt: "Well casing and pump components exposed during professional pump repair" },
  { icon: Zap, title: "Well Pump Installation", desc: "Professional installation and replacement of well pump systems designed for Pocono Mountain conditions.", href: "/services/well-pump-installation", img: wellPullingImg, imgAlt: "Well pump pulling equipment used for submersible pump installation and replacement" },
  { icon: AlertTriangle, title: "Grinder Pump Sales & Service", desc: "Residential and commercial grinder pump sales, installation, and repair — including commercial pump stations.", href: "/services/grinder-pump-repair", img: grinderInstallImg, imgAlt: "Excavator positioning grinder pump station at residential property in the Poconos" },
  { icon: Droplets, title: "Sewage Pump Systems", desc: "Complete sewage and wastewater pump service, installation, and maintenance.", href: "/services/sewage-pump-systems", img: sewagePumpImg, imgAlt: "New sewage pump basin being crane-lifted into excavated position for installation" },
  { icon: Filter, title: "Water Filtration Systems", desc: "Whole-house water filtration and treatment systems for clean, safe well water.", href: "/services/water-filtration", img: openWellImg, imgAlt: "Open well showing water conditions that require professional filtration solutions" },
  { icon: Droplets, title: "Hydro-Fracking", desc: "Restore flow to low yielding or dry wells with high-pressure hydro-fracking — boost production without drilling new.", href: "/services/hydro-fracking", img: openWellImg, imgAlt: "Well water conditions where hydro-fracking can restore production for low yielding wells" },
  { icon: Phone, title: "Emergency Pump Service", desc: "Same-day emergency response when you lose water. We prioritize no-water calls.", href: "/services/emergency-pump-service", img: truckImg, imgAlt: "Semper Fi emergency service truck ready for same-day well pump repair dispatch" },
];

const Services = () => (
  <Layout>
    <SeoHead
      title="Well Pump & Water System Services – 33 Years Trusted | Semper Fi Pump Service"
      description="Complete well pump, grinder pump, hydro-fracking, and water system services in the Pocono Mountains. Residential & commercial. Call for a free estimate."
    />
    <section className="bg-primary">
      <div className="container py-16 md:py-24">
        <h1 className="text-3xl md:text-4xl font-black text-primary-foreground mb-4 border-l-0 pl-0">Our Services</h1>
        <p className="text-primary-foreground/80 max-w-2xl text-pretty">Complete well pump, grinder pump, and water system services for homes and commercial properties across the Pocono Mountains. 33 years of experience you can count on.</p>
      </div>
    </section>

    <section className="container py-16 md:py-24">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <Link key={service.title} to={service.href} className="group bg-card rounded-sm shadow-card hover:shadow-elevated transition-all duration-200 flex flex-col border border-transparent hover:border-accent/20 overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={service.img} alt={service.imgAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
            </div>
            <div className="p-6 flex flex-col flex-1">
              <div className="flex items-center gap-2 mb-3">
                <service.icon className="w-5 h-5 text-accent shrink-0" />
                <h2 className="text-lg font-bold border-l-0 pl-0 group-hover:text-accent transition-colors">{service.title}</h2>
              </div>
              <p className="text-sm text-muted-foreground text-pretty mb-4 leading-relaxed flex-1">{service.desc}</p>
              <span className="text-sm font-bold text-accent inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all mt-auto">
                Learn More <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>

    <section className="bg-primary">
      <div className="container py-16 text-center">
        <h2 className="text-2xl font-black text-primary-foreground mb-4 border-l-0 pl-0 inline-block">Need Emergency Service?</h2>
        <p className="text-primary-foreground/80 mb-8 max-w-lg mx-auto text-pretty">Don't wait. Call now and speak to a real technician who knows your area.</p>
        <Button asChild variant="emergency" size="xl">
          <a href={PHONE_HREF}><Phone className="w-5 h-5" />Call {PHONE_NUMBER}</a>
        </Button>
      </div>
    </section>
  </Layout>
);

export default Services;
